import React, { useCallback, useMemo } from "react";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import useDebugInformation from "../../../../../../../../../hooks/useDebugInformation";
import { ui } from "../../../../../../../../../interfaces/interfaces";
import { getIcon, a11yProps, getLabel } from "./helpers";
import { KeysForCustomerSideTabs, useCarePlanCustomerSideTab } from "../../../../../../../../../hooks/sideTabs/useCarePlanCustomerSideTab";

/**
 * Component for rendering side tabs in the Service Agreement Customer UI.
 * 
 * @param {Object} props - Component props
 * @param {string} props.parent - The parent component name
 */
const SideTabs: React.FC<Props> = ({ parent }) => {
    const { serviceAgreementCustomerTab, sideTabsIndexes, sideTabsStates } = useCarePlanCustomerSideTab();

    const debugName = `${parent}▷[SideTabs]`;

    useDebugInformation(debugName, { name: 'SideTabs', serviceAgreementCustomerTab, sideTabsIndexes, sideTabsStates });

    /**
     * Handles tab change events.
     * 
     * @param {React.SyntheticEvent} event - The event object
     * @param {number} value - The new tab index
     */
    const handleChange = useCallback((event: React.SyntheticEvent<Element, Event>, value: any) => {
        if (value !== undefined) ui.CarePlanCustomerActiveSideTab = value;
    }, []);

    const renderedTabs = useMemo(() => Object.entries(sideTabsIndexes).map(([key, value]) => (
        <Tab
            key={`tab-${key}-${value}`}
            label={getLabel(key)}
            {...a11yProps(value)}
            wrapped
            icon={getIcon(value, sideTabsStates[key as KeysForCustomerSideTabs])}
            iconPosition="top"
        />
    )), [sideTabsIndexes, sideTabsStates]);

    return (
        <Tabs
            orientation="vertical"
            variant="scrollable"
            value={serviceAgreementCustomerTab}
            onChange={handleChange}
            aria-label="Vertical tabs for Service Agreement Customer"
            sx={{ borderRight: 1, borderColor: 'divider', button: { maxWidth: 75, minWidth: 75 } }}>
            {renderedTabs}
        </Tabs>
    );
};

export default SideTabs;

/**
 * Prop types for the SideTabs component.
 */
type Props = {
    parent: string,
};

